import type { ReactNode } from "react";
import { AlertTriangle, Inbox } from "lucide-react";
import { Button } from "./Button";
import { cn } from "../lib/cn";

interface StateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  compact?: boolean;
}

export function EmptyState({ icon, title, description, action, secondaryAction, compact }: StateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 border-2 border-dashed border-border text-center rounded-xl", compact ? "px-6 py-8" : "px-6 py-16")}>
      <div className="text-muted-foreground">{icon ?? <Inbox className={compact ? "h-8 w-8" : "h-10 w-10"} />}</div>
      <div className="space-y-1">
        <h3 className={cn("font-semibold", compact ? "text-sm" : "text-base")}>{title}</h3>
        {description && <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>{description}</p>}
      </div>
      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          {action && (
            <Button variant="outline" size={compact ? "sm" : "md"} onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" size={compact ? "sm" : "md"} onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description,
  action,
  secondaryAction,
  compact,
}: Partial<StateProps>) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 text-center", compact ? "px-6 py-8" : "px-6 py-16")}>
      <AlertTriangle className={cn("text-destructive", compact ? "h-8 w-8" : "h-10 w-10")} />
      <div className="space-y-1">
        <h3 className={cn("font-semibold text-destructive", compact ? "text-sm" : "text-base")}>{title}</h3>
        {description && <p className={cn("text-muted-foreground", compact ? "text-xs" : "text-sm")}>{description}</p>}
      </div>
      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          {action && (
            <Button variant="outline" size={compact ? "sm" : "md"} onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" size={compact ? "sm" : "md"} onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
